/**
 * AdminReferrals — referral codes (sales reps + owner-refer-owner) with the
 * per-code acquisition funnel: visitors → leads → owner signups → activated
 * ($100/mo paid). Pretty link per code: sales.rinsepay.app/<code>.
 * Tapping a code swaps the lower card for its referred-owners drill-down.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, ActivityIndicator, TouchableOpacity, Modal, TextInput } from 'react-native';
import { Redirect, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';

import * as Clipboard from 'expo-clipboard';

import { api } from '../../src/api/client';
import { config } from '../../src/config';
import QrSvgView from '../../src/components/QrSvgView';
import { useAdminPermissions } from '../../src/hooks/useAdminPermissions';
import { AdminToolbar } from '../../src/components/AdminList';
import ContentContainer from '../../src/components/ContentContainer';
import StageBadge from '../../src/components/StageBadge';
import Button from '../../src/components/Button';
import { useToast } from '../../src/components/Toast';
import { useTheme } from '../../src/context/ThemeContext';
import { useResponsive } from '../../src/utils/responsive';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import type { ColorTheme } from '../../src/utils/theme';

const MOBILE_BREAKPOINT = 768;

// Debug builds point at api.rinsepay.design → show the dev sales host in links.
const SALES_HOST = config.apiUrl.includes('rinsepay.design') ? 'sales.rinsepay.design' : 'sales.rinsepay.app';

interface CodeRow {
  id: string; code: string; type: 'sales_rep' | 'owner'; displayName: string;
  ownerId: string | null; notes: string | null; isActive: boolean; createdAt: string;
  commissionRateBps: number | null; commissionDurationMonths: number | null; commissionStartsAt: string | null;
  visitors: number; pageviews: number; leads: number; signups: number; activated: number;
}

/** 500 bps → "5%", 250 → "2.5%". */
const fmtRate = (bps: number) => `${(bps / 100).toFixed(2).replace(/\.?0+$/, '')}%`;
interface ReferredOwnerRow {
  id: string; businessName: string; email: string; createdAt: string; attributedAt: string | null;
  stripeOnboarded: boolean; activated: boolean; activatedAt: string | null;
}

const TYPE_FILTERS = [
  { label: 'All', value: '' },
  { label: 'Sales reps', value: 'sales_rep' },
  { label: 'Owners', value: 'owner' },
] as const;

const fmtDate = (d: string) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

export default function AdminReferrals() {
  const { has, loaded } = useAdminPermissions();
  if (loaded && !has('platform_referrals')) return <Redirect href="/admin/users" />;
  const canWrite = has('platform_referrals', 'write');
  const { colors } = useTheme();
  const { width } = useResponsive();
  const isMobile = width < MOBILE_BREAKPOINT;
  const styles = useStyles(colors, isMobile);
  const toast = useToast();
  const router = useRouter();

  const [rows, setRows] = useState<CodeRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [showInactive, setShowInactive] = useState(false);

  // Create / edit modal
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<CodeRow | null>(null);
  const [formCode, setFormCode] = useState('');
  const [formName, setFormName] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const [formActive, setFormActive] = useState(true);
  const [formRatePercent, setFormRatePercent] = useState('');
  const [formDurationMonths, setFormDurationMonths] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  // Printable QR modal
  const [qrModal, setQrModal] = useState<{ code: string; url: string; qrSvg: string } | null>(null);
  const [qrLoadingId, setQrLoadingId] = useState<string | null>(null);

  // Drill-down
  const [selected, setSelected] = useState<CodeRow | null>(null);
  const [refRows, setRefRows] = useState<ReferredOwnerRow[]>([]);
  const [refTotal, setRefTotal] = useState(0);
  const [refPage, setRefPage] = useState(1);
  const [refSearch, setRefSearch] = useState('');
  const [refLoading, setRefLoading] = useState(false);

  const fetchCodes = useCallback(async (p = 1, s = '', type = '', inactive = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(p), limit: '10' });
      if (s) params.set('search', s);
      if (type) params.set('type', type);
      if (inactive) params.set('includeInactive', '1');
      const res = await api<{ rows: CodeRow[]; total: number }>(`/api/admin/referrals?${params}`);
      setRows(res.rows);
      setTotal(res.total);
      setPage(p);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  const fetchDrilldown = useCallback(async (codeId: string, p = 1, s = '') => {
    setRefLoading(true);
    try {
      const params = new URLSearchParams({ page: String(p), limit: '10' });
      if (s) params.set('search', s);
      const res = await api<{ rows: ReferredOwnerRow[]; total: number }>(`/api/admin/referrals/${codeId}?${params}`);
      setRefRows(res.rows);
      setRefTotal(res.total);
      setRefPage(p);
    } catch (err) { console.error(err); }
    finally { setRefLoading(false); }
  }, []);

  useEffect(() => { fetchCodes(1, search, typeFilter, showInactive); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter, showInactive]);

  const openCreate = () => {
    setEditing(null); setFormCode(''); setFormName(''); setFormNotes(''); setFormActive(true); setFormError('');
    setFormRatePercent(''); setFormDurationMonths('');
    setShowForm(true);
  };
  const openEdit = (row: CodeRow) => {
    setEditing(row); setFormCode(row.code); setFormName(row.displayName); setFormNotes(row.notes || ''); setFormActive(row.isActive); setFormError('');
    setFormRatePercent(row.commissionRateBps != null ? String(row.commissionRateBps / 100) : '');
    setFormDurationMonths(row.commissionDurationMonths != null ? String(row.commissionDurationMonths) : '');
    setShowForm(true);
  };

  /** Percent text → basis points (or null for blank). Throws a message on garbage. */
  const commissionBody = () => {
    const rateTxt = formRatePercent.trim();
    const durTxt = formDurationMonths.trim();
    let rateBps: number | null = null;
    if (rateTxt) {
      const pct = Number(rateTxt);
      if (!Number.isFinite(pct) || pct <= 0 || pct > 100) throw new Error('Commission rate must be a percentage between 0.01 and 100');
      rateBps = Math.round(pct * 100);
    }
    let duration: number | null = null;
    if (durTxt) {
      const n = Number(durTxt);
      if (!Number.isInteger(n) || n < 1) throw new Error('Duration must be a whole number of months (blank = forever)');
      duration = n;
    }
    return { commissionRateBps: rateBps, commissionDurationMonths: duration };
  };

  const handleSave = async () => {
    setFormError('');
    setSaving(true);
    try {
      const isRep = editing ? editing.type === 'sales_rep' : true;
      const commission = isRep ? commissionBody() : {};
      if (editing) {
        await api(`/api/admin/referrals/${editing.id}`, { method: 'PATCH', body: { displayName: formName.trim(), notes: formNotes, isActive: formActive, ...commission } });
        toast.show({ type: 'success', title: 'Referral code updated' });
      } else {
        await api('/api/admin/referrals', { method: 'POST', body: { code: formCode.trim(), displayName: formName.trim(), notes: formNotes.trim() || undefined, ...commission } });
        toast.show({ type: 'success', title: 'Referral code created' });
      }
      setShowForm(false);
      fetchCodes(page, search, typeFilter, showInactive);
      if (selected) fetchDrilldown(selected.id, refPage, refSearch);
    } catch (err: any) { setFormError(err.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleCopyLink = async (row: CodeRow) => {
    const url = `https://${SALES_HOST}/${row.code}`;
    try {
      await Clipboard.setStringAsync(url);
      toast.show({ type: 'success', title: 'Link copied', message: url });
    } catch {
      toast.show({ type: 'error', title: 'Copy failed', message: url });
    }
  };

  const handleShowQr = async (row: CodeRow) => {
    setQrLoadingId(row.id);
    try {
      const res = await api<{ code: string; url: string; qrSvg: string }>(`/api/admin/referrals/${row.id}/qr`);
      setQrModal(res);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'QR failed', message: err.message });
    } finally { setQrLoadingId(null); }
  };

  const openDrilldown = (row: CodeRow) => {
    setSelected(row);
    setRefSearch('');
    fetchDrilldown(row.id, 1, '');
  };

  if (loading && rows.length === 0 && !selected) {
    return <View style={styles.center}><ActivityIndicator size="large" color={colors.primary} /></View>;
  }

  const csvEndpoint = `/api/admin/referrals?${new URLSearchParams({ ...(search && { search }), ...(typeFilter && { type: typeFilter }), ...(showInactive && { includeInactive: '1' }) })}`;

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <ContentContainer role="admin">

        <View style={styles.headerRow}>
          <Text style={styles.pageTitle}>Referrals</Text>
          {canWrite && (
            <Button
              title="New Rep Code"
              size="sm"
              variant="outline"
              icon={<Ionicons name="add" size={16} color={colors.primary} />}
              onPress={openCreate}
            />
          )}
        </View>
        <Text style={styles.pageSub}>
          Hand out {SALES_HOST}/&lt;code&gt; links or have prospects type the code at signup.
          Activated = first $100/mo platform fee paid.
        </Text>

        {/* Filters */}
        <View style={styles.chipRow}>
          {TYPE_FILTERS.map(f => (
            <TouchableOpacity
              key={f.value}
              style={[styles.chip, typeFilter === f.value && styles.chipActive]}
              onPress={() => { setSelected(null); setTypeFilter(f.value); }}
            >
              <Text style={[styles.chipText, typeFilter === f.value && styles.chipTextActive]}>{f.label}</Text>
            </TouchableOpacity>
          ))}
          <TouchableOpacity
            style={[styles.chip, showInactive && styles.chipActive]}
            onPress={() => { setSelected(null); setShowInactive(!showInactive); }}
          >
            <Text style={[styles.chipText, showInactive && styles.chipTextActive]}>Show inactive</Text>
          </TouchableOpacity>
        </View>

        {/* Codes list */}
        <View style={styles.card}>
          <AdminToolbar
            search={search}
            onSearchChange={setSearch}
            onSearch={() => { setSelected(null); fetchCodes(1, search, typeFilter, showInactive); }}
            total={total}
            page={page}
            limit={10}
            onPageChange={(p) => fetchCodes(p, search, typeFilter, showInactive)}
            csvEndpoint={csvEndpoint}
            csvFilename="referrals.csv"
            label="code"
          />
          {loading ? (
            <ActivityIndicator size="small" color={colors.primary} style={{ marginVertical: spacing.lg }} />
          ) : rows.length === 0 ? (
            <Text style={styles.empty}>No referral codes yet{canWrite ? ' — create one for your first sales rep' : ''}</Text>
          ) : (
            rows.map(r => (
              <TouchableOpacity key={r.id} style={[styles.listRow, selected?.id === r.id && styles.listRowSelected]} onPress={() => openDrilldown(r)}>
                <View style={styles.listBody}>
                  <View style={styles.codeLine}>
                    <Text style={styles.codeText}>{r.code}</Text>
                    <View style={[styles.typeBadge, { backgroundColor: r.type === 'sales_rep' ? colors.primaryBg : colors.surfaceSecondary }]}>
                      <Text style={[styles.typeBadgeText, r.type === 'sales_rep' && { color: colors.primary }]}>
                        {r.type === 'sales_rep' ? 'Sales rep' : 'Owner'}
                      </Text>
                    </View>
                    {!r.isActive && (
                      <View style={[styles.typeBadge, { backgroundColor: colors.danger + '15' }]}>
                        <Text style={[styles.typeBadgeText, { color: colors.danger }]}>Inactive</Text>
                      </View>
                    )}
                  </View>
                  <Text style={styles.listSub} numberOfLines={1}>{r.displayName}</Text>
                  <Text style={styles.listMeta} numberOfLines={1}>{SALES_HOST}/{r.code} · created {fmtDate(r.createdAt)}</Text>
                  {r.commissionRateBps != null && (
                    <Text style={[styles.listMeta, { color: colors.primary }]} numberOfLines={1}>
                      Commission {fmtRate(r.commissionRateBps)} · {r.commissionDurationMonths != null ? `${r.commissionDurationMonths} mo per customer` : 'forever'}
                    </Text>
                  )}
                  {r.notes ? <Text style={styles.listMeta} numberOfLines={1}>{r.notes}</Text> : null}
                </View>
                <View style={styles.statsCol}>
                  <View style={styles.statPair}><Text style={styles.statValue}>{r.visitors}</Text><Text style={styles.statLabel}>visitors</Text></View>
                  <View style={styles.statPair}><Text style={styles.statValue}>{r.leads}</Text><Text style={styles.statLabel}>leads</Text></View>
                  <View style={styles.statPair}><Text style={styles.statValue}>{r.signups}</Text><Text style={styles.statLabel}>signups</Text></View>
                  <View style={styles.statPair}><Text style={[styles.statValue, r.activated > 0 && { color: colors.success }]}>{r.activated}</Text><Text style={styles.statLabel}>activated</Text></View>
                </View>
                <TouchableOpacity style={styles.editBtn} onPress={(e) => { e.stopPropagation?.(); handleCopyLink(r); }} accessibilityLabel={`Copy link for ${r.code}`}>
                  <Ionicons name="link" size={16} color={colors.primary} />
                </TouchableOpacity>
                <TouchableOpacity style={styles.editBtn} onPress={(e) => { e.stopPropagation?.(); handleShowQr(r); }} accessibilityLabel={`Show QR for ${r.code}`}>
                  {qrLoadingId === r.id
                    ? <ActivityIndicator size="small" color={colors.primary} />
                    : <Ionicons name="qr-code" size={16} color={colors.primary} />}
                </TouchableOpacity>
                {canWrite && (
                  <TouchableOpacity style={styles.editBtn} onPress={(e) => { e.stopPropagation?.(); openEdit(r); }}>
                    <Ionicons name="pencil" size={16} color={colors.primary} />
                  </TouchableOpacity>
                )}
              </TouchableOpacity>
            ))
          )}
        </View>

        {/* Drill-down */}
        {selected && (
          <>
            <View style={styles.headerRow}>
              <Text style={styles.sectionTitle}>Referred by “{selected.code}” — {selected.displayName}</Text>
              <TouchableOpacity onPress={() => setSelected(null)}>
                <Ionicons name="close-circle" size={22} color={colors.textSecondary} />
              </TouchableOpacity>
            </View>
            <View style={styles.card}>
              <AdminToolbar
                search={refSearch}
                onSearchChange={setRefSearch}
                onSearch={() => fetchDrilldown(selected.id, 1, refSearch)}
                total={refTotal}
                page={refPage}
                limit={10}
                onPageChange={(p) => fetchDrilldown(selected.id, p, refSearch)}
                csvEndpoint={`/api/admin/referrals/${selected.id}?${new URLSearchParams({ ...(refSearch && { search: refSearch }) })}`}
                csvFilename={`referrals-${selected.code}.csv`}
                label="owner"
              />
              {refLoading ? (
                <ActivityIndicator size="small" color={colors.primary} style={{ marginVertical: spacing.lg }} />
              ) : refRows.length === 0 ? (
                <Text style={styles.empty}>No owner signups attributed to this code yet</Text>
              ) : (
                refRows.map(o => (
                  <TouchableOpacity key={o.id} style={styles.listRow} onPress={() => router.push(`/admin/owner/${o.id}` as never)}>
                    <View style={styles.listBody}>
                      <Text style={styles.listName} numberOfLines={1}>{o.businessName}</Text>
                      <Text style={styles.listSub} numberOfLines={1}>{o.email}</Text>
                      <Text style={styles.listMeta} numberOfLines={1}>Signed up {fmtDate(o.createdAt)}</Text>
                      <View style={styles.badgeRow}>
                        <StageBadge label="Stripe" on={o.stripeOnboarded} />
                        <StageBadge label="Activated" on={o.activated} />
                      </View>
                    </View>
                    <View style={styles.listRight}>
                      {o.activatedAt
                        ? <><Text style={[styles.statValue, { color: colors.success }]}>{fmtDate(o.activatedAt)}</Text><Text style={styles.statLabel}>activated</Text></>
                        : <Text style={styles.listMeta}>not activated</Text>}
                    </View>
                  </TouchableOpacity>
                ))
              )}
            </View>
          </>
        )}

        {/* Printable QR modal */}
        <Modal visible={!!qrModal} transparent animationType="fade" onRequestClose={() => setQrModal(null)}>
          <View style={styles.modalOverlay}>
            <View style={[styles.modalCard, { alignItems: 'center' }]}>
              {qrModal && (
                <>
                  <Text style={styles.modalTitle}>{qrModal.code}</Text>
                  <View style={{ width: '100%', maxWidth: 320 }}>
                    <QrSvgView uri={qrModal.qrSvg} width={300} accessibilityLabel={`Referral QR for ${qrModal.code}`} />
                  </View>
                  <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginTop: spacing.sm, textAlign: 'center' }}>{qrModal.url}</Text>
                  <View style={styles.modalActions}>
                    <Button title="Copy Link" variant="outline" size="sm" style={{ flex: 1 }}
                      icon={<Ionicons name="link" size={16} color={colors.primary} />}
                      onPress={async () => { try { await Clipboard.setStringAsync(qrModal.url); toast.show({ type: 'success', title: 'Link copied' }); } catch { toast.show({ type: 'error', title: 'Copy failed' }); } }} />
                    <Button title="Close" size="sm" style={{ flex: 1 }} onPress={() => setQrModal(null)} />
                  </View>
                </>
              )}
            </View>
          </View>
        </Modal>

        {/* Create / edit modal */}
        <Modal visible={showForm} transparent animationType="fade" onRequestClose={() => setShowForm(false)}>
          <View style={styles.modalOverlay}>
            <View style={styles.modalCard}>
              <Text style={styles.modalTitle}>{editing ? `Edit “${editing.code}”` : 'New Sales Rep Code'}</Text>
              {!editing && (
                <>
                  <Text style={styles.fieldLabel}>Code (becomes {SALES_HOST}/&lt;code&gt;)</Text>
                  <TextInput
                    style={styles.input}
                    value={formCode}
                    onChangeText={(t) => setFormCode(t.toLowerCase())}
                    placeholder="brandon"
                    placeholderTextColor={colors.textLight}
                    autoCapitalize="none"
                    autoCorrect={false}
                    maxLength={32}
                  />
                </>
              )}
              <Text style={styles.fieldLabel}>Display name</Text>
              <TextInput
                style={styles.input}
                value={formName}
                onChangeText={setFormName}
                placeholder="Brandon Smith"
                placeholderTextColor={colors.textLight}
                maxLength={64}
              />
              {(editing ? editing.type === 'sales_rep' : true) && (
                <>
                  <View style={styles.commissionRow}>
                    <View style={{ flex: 1 }}>
                      <Text style={styles.fieldLabel}>Commission rate (%)</Text>
                      <TextInput
                        style={styles.input}
                        value={formRatePercent}
                        onChangeText={setFormRatePercent}
                        placeholder="e.g. 5"
                        placeholderTextColor={colors.textLight}
                        keyboardType="decimal-pad"
                        maxLength={6}
                      />
                    </View>
                    <View style={{ flex: 1 }}>
                      <Text style={styles.fieldLabel}>Duration (months)</Text>
                      <TextInput
                        style={styles.input}
                        value={formDurationMonths}
                        onChangeText={setFormDurationMonths}
                        placeholder="blank = forever"
                        placeholderTextColor={colors.textLight}
                        keyboardType="number-pad"
                        maxLength={4}
                      />
                    </View>
                  </View>
                  <Text style={styles.fieldHint}>
                    Rep earns this % of RinsePay's revenue from each customer they bring in (the $100/mo platform fee plus our 1% transaction fees) for the duration, counted from that customer's activation. Accrues from today forward, never retroactively.
                  </Text>
                </>
              )}
              <Text style={styles.fieldLabel}>Notes (territory, agreement details, …)</Text>
              <TextInput
                style={[styles.input, styles.inputMultiline]}
                value={formNotes}
                onChangeText={setFormNotes}
                placeholder="Optional"
                placeholderTextColor={colors.textLight}
                multiline
                maxLength={500}
              />
              {editing && (
                <TouchableOpacity style={styles.activeRow} onPress={() => setFormActive(!formActive)}>
                  <Ionicons name={formActive ? 'checkbox' : 'square-outline'} size={20} color={colors.primary} />
                  <Text style={styles.activeText}>Active (attributes new signups)</Text>
                </TouchableOpacity>
              )}
              {formError ? <Text style={styles.formError}>{formError}</Text> : null}
              <View style={styles.modalActions}>
                <Button title="Cancel" variant="outline" size="sm" onPress={() => setShowForm(false)} style={{ flex: 1 }} />
                <Button title={editing ? 'Save' : 'Create'} size="sm" onPress={handleSave} loading={saving} style={{ flex: 1 }} />
              </View>
            </View>
          </View>
        </Modal>

      </ContentContainer>
    </ScrollView>
  );
}

const createStyles = (c: ColorTheme, isMobile: boolean) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: isMobile ? spacing.md : spacing.lg },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  headerRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: spacing.xs, gap: spacing.sm },
  pageTitle: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.text },
  pageSub: { fontSize: fontSize.sm, color: c.textSecondary, marginBottom: spacing.md, lineHeight: 20 },
  sectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.bold, color: c.text, flex: 1 },
  empty: { textAlign: 'center', color: c.textSecondary, paddingVertical: spacing.md, fontSize: fontSize.sm },

  chipRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, marginBottom: spacing.md, flexWrap: 'wrap' },
  chip: {
    paddingHorizontal: spacing.sm, paddingVertical: 6,
    borderRadius: borderRadius.sm, backgroundColor: c.surface,
    borderWidth: 1, borderColor: c.borderLight,
  },
  chipActive: { backgroundColor: c.primaryBg, borderColor: c.primary },
  chipText: { fontSize: fontSize.xs, color: c.textSecondary, fontWeight: fontWeight.medium },
  chipTextActive: { color: c.primary, fontWeight: fontWeight.semibold },

  card: {
    backgroundColor: c.surface, borderRadius: borderRadius.lg,
    padding: spacing.md, marginBottom: spacing.md,
    shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 3, elevation: 1,
  },

  listRow: {
    flexDirection: 'row', alignItems: 'center',
    paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: c.borderLight, gap: spacing.sm,
  },
  listRowSelected: { backgroundColor: c.primaryBg, borderRadius: borderRadius.sm },
  listBody: { flex: 1, minWidth: 0 },
  codeLine: { flexDirection: 'row', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  codeText: { fontSize: fontSize.sm, fontWeight: fontWeight.bold, color: c.text, fontFamily: undefined },
  typeBadge: { paddingHorizontal: 6, paddingVertical: 2, borderRadius: borderRadius.sm },
  typeBadgeText: { fontSize: 10, fontWeight: fontWeight.semibold, color: c.textSecondary },
  listName: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },
  listSub: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 1 },
  listMeta: { fontSize: fontSize.xs, color: c.textLight, marginTop: 2 },
  badgeRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 4, marginTop: 6 },
  listRight: { alignItems: 'flex-end', minWidth: 80 },

  statsCol: { flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 2 : spacing.md, alignItems: 'flex-end' },
  statPair: { alignItems: isMobile ? 'flex-end' : 'center', minWidth: isMobile ? undefined : 52 },
  statValue: { fontSize: fontSize.sm, fontWeight: fontWeight.bold, color: c.text, fontVariant: ['tabular-nums'] },
  statLabel: { fontSize: 10, color: c.textLight },
  editBtn: { padding: spacing.xs },

  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.4)', justifyContent: 'center', alignItems: 'center', padding: spacing.lg },
  modalCard: { backgroundColor: c.surface, borderRadius: borderRadius.lg, padding: spacing.lg, width: '100%', maxWidth: 420 },
  modalTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.md },
  fieldLabel: { fontSize: fontSize.xs, fontWeight: fontWeight.medium, color: c.textSecondary, marginBottom: 4, marginTop: spacing.sm },
  fieldHint: { fontSize: fontSize.xs, color: c.textLight, marginTop: 6, lineHeight: 16 },
  commissionRow: { flexDirection: 'row', gap: spacing.sm },
  input: {
    borderWidth: 1, borderColor: c.border, borderRadius: borderRadius.sm,
    paddingHorizontal: spacing.sm, paddingVertical: 8, fontSize: fontSize.sm, color: c.text, backgroundColor: c.background,
  },
  inputMultiline: { minHeight: 60, textAlignVertical: 'top' },
  activeRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, marginTop: spacing.md },
  activeText: { fontSize: fontSize.sm, color: c.text },
  formError: { fontSize: fontSize.sm, color: c.danger, marginTop: spacing.sm },
  modalActions: { flexDirection: 'row', gap: spacing.sm, marginTop: spacing.lg },
});

const useStyles = (c: ColorTheme, isMobile: boolean) => React.useMemo(() => createStyles(c, isMobile), [c, isMobile]);
